import { useModal } from "@context/ModalContext";
import { FC, useEffect, useState, useRef } from "react";
import { useAuth } from "@context/AuthContext";
import { navigate } from "vike/client/router";
import { usePageContext } from "vike-react/usePageContext";
import NavBar from "./_components/NavBar";
import Hero from "./_components/Hero";
import ContentPage from "./_components/ContentPage";
import GridPage from "./_components/GridPage";
import GridPage2 from "./_components/GridPage2";
import GridPage3 from "./_components/GridPage3";
import GridPage4 from "./_components/GridPage4";
import Footer from "@/components/LandingPage/Footer";
import Vision from "./_components/Vision";

const Page: FC = () => {
	const { isModalOpen, setModalState } = useModal();
	const { user, isLoading, isAuthenticated } = useAuth();
	const pageContext = usePageContext();
	const [isRedirecting, setIsRedirecting] = useState(false);
	const [theme, setTheme] = useState<"light" | "dark">("dark");

	// Refs for scroll animations
	const heroRef = useRef<HTMLDivElement>(null);
	const boardRef = useRef<HTMLDivElement>(null);
	const [scrollProgress, setScrollProgress] = useState(0);

	useEffect(() => {
		// Check if there's a returnTo parameter in the URL (from OAuth callback)
		if (!isLoading) {
			const urlParams = new URLSearchParams(window.location.search);
			const returnTo = urlParams.get('returnTo');

			if (returnTo) {
				const decodedReturnTo = decodeURIComponent(returnTo);
				urlParams.delete('returnTo');
				const newSearch = urlParams.toString();

				if (decodedReturnTo !== window.location.pathname) {
					setIsRedirecting(true);
					const newUrl = decodedReturnTo + (newSearch ? `?${newSearch}` : '');
					navigate(newUrl);
				} else {
					window.history.replaceState({}, '', window.location.pathname + (newSearch ? `?${newSearch}` : ''));
				}
			}
		}
	}, [isLoading]);

	// Scroll animation effect
	useEffect(() => {
		// Wait for the DOM to be ready
		const attachScrollListener = () => {
			const scrollContainer = heroRef.current;
			if (!scrollContainer) {
				setTimeout(attachScrollListener, 100);
				return;
			}

			const handleScroll = () => {
				const scrollY = scrollContainer.scrollTop;
				const maxScroll = window.innerHeight;
				const progress = Math.min(scrollY / maxScroll, 1);
				setScrollProgress(progress);

				// Determine theme based on scroll position
				const gridPages = scrollContainer.querySelectorAll('.snap-start');

				// Calculate which section is currently in view
				const viewportCenter = scrollY + (window.innerHeight / 2);

				// Check if we're in a white background section
				let inLightSection = false;

				// Check each section to see if it has white background and is in viewport
				gridPages.forEach((section, index) => {
					const element = section as HTMLElement;
					const sectionTop = element.offsetTop;
					const sectionBottom = sectionTop + element.offsetHeight;

					// Check if viewport center is in this section
					if (viewportCenter >= sectionTop && viewportCenter < sectionBottom) {
						// Check if this section has white background
						const bgColor = window.getComputedStyle(element).backgroundColor;
						// White backgrounds will be 'rgb(255, 255, 255)' or similar
						if (bgColor === 'rgb(255, 255, 255)' || element.classList.contains('bg-white')) {
							inLightSection = true;
						}
					}
				});

				setTheme(inLightSection ? 'light' : 'dark');
			};

			scrollContainer.addEventListener('scroll', handleScroll);

			// Cleanup function
			return () => {
				scrollContainer.removeEventListener('scroll', handleScroll);
			};
		};

		const cleanup = attachScrollListener();
		return cleanup;
	}, [heroRef]);

	if (isRedirecting || isLoading) {
		return null;
	}

	const onLogin = () => {
		navigate("/search");
	};

	// Calculate transform values based on scroll progress
	const boardScale = 0.7 + (scrollProgress * 0.3); // Scale from 0.7 to 1
	const boardTranslateY = (1 - scrollProgress) * 200; // Move up 200px
	const boardOpacity = 0.3 + (scrollProgress * 0.7); // Fade from 0.3 to 1

	return (
		<>
			<NavBar theme={theme} />
			<div
				ref={heroRef}
				className="w-full h-dvh bg-black overflow-y-scroll overflow-x-hidden snap-y snap-mandatory"
			>
				{/* Hero Section */}
				<Hero scrollProgress={scrollProgress} />
				{/* Additional content to enable scrolling */}
				<div className="w-full min-h-dvh bg-black snap-start relative">

					{/* Desktop-only: Animated bottom panel - transitions from fixed to absolute after scroll animations */}
					<div
						className="hidden md:flex left-1/2 -translate-x-1/2 z-30 flex-col items-center justify-center transition-all duration-300 ease-out pointer-events-none"
						style={{
							// Switch from fixed to absolute positioning once scroll animations complete
							position: scrollProgress >= 1 ? 'absolute' : 'fixed',
							// When absolute, position at the bottom of this black section
							bottom: scrollProgress >= 1
								? '52px'
								: `${scrollProgress * 52}px`,
							// Desktop - Closed: viewport - 216px (108px each side), Open: viewport - 84px (42px each side)
							width: typeof window !== 'undefined'
								? `${window.innerWidth - (216 - (216 - 84) * scrollProgress)}px`
								: '100%',
							// Height: grows from 80px to (viewport height - navbar (105px) - bottom margin (52px))
							height: typeof window !== 'undefined'
								? `${80 + (scrollProgress * (window.innerHeight - 105 - 52 - 80))}px`
								: '80px'
						}}
					>
						{/* Curo mascot - fades out as scroll progresses */}
						<img
							src="/curohome.svg"
							className="w-[180px] object-cover absolute left-1/2 -translate-x-1/2 transition-opacity translate-y-[40px] !duration-[50ms]"
							style={{
								bottom: '80px',
								opacity: 1 - scrollProgress
							}}
						/>

						<div
							className="w-full relative h-full landing-bg-radial-gradient overflow-hidden"
							style={{
								borderTopLeftRadius: `${39.2 - (5.41 * scrollProgress)}px`,
								borderTopRightRadius: `${39.2 - (5.41 * scrollProgress)}px`,
								borderBottomLeftRadius: `${33.79 * scrollProgress}px`,
								borderBottomRightRadius: `${33.79 * scrollProgress}px`
							}}
						>
							{/* Content that fades in as panel expands */}
							<div
								className="w-full h-full flex flex-col items-center justify-between pt-[160px] transition-opacity duration-50"
								style={{
									opacity: scrollProgress > 0.5 ? (scrollProgress - 0.5) * 2 : 0
								}}
							>
								<div className="flex flex-col gap-3">
									<h1 className="font-ibmPlexSerif text-center font-semibold italic text-[32px] leading-[48px] tracking-[2%] text-[#EEEEEE]">
										A Companion
									</h1>
									<p className="font-satoshi font-light text-[24px] leading-[40px] tracking-[1%] text-center text-[#DDDDDD]">
										Who makes learning online Faster, Organized and Interactive.
									</p>
								</div>

								<div className="flex flex-row items-center justify-center w-full bg-gradient-to-b from-transparent to-[#36150194]">
									<img src="/landingpage/geometry.svg" alt="Geometry pattern" />
								</div>
							</div>
						</div>
					</div>

					{/* Mobile-only: Static expanded panel — no scroll-driven JS, no rerenders */}
					<div className="md:hidden w-full h-dvh flex items-end pb-4">
						<div
							className="w-full mx-4 landing-bg-radial-gradient overflow-hidden flex flex-col items-center justify-between pt-[160px]"
							style={{
								height: 'calc(100dvh - 105px - 16px)',
								borderRadius: '33.79px'
							}}
						>
							<div className="flex flex-col gap-3 px-4">
								<h1 className="font-ibmPlexSerif text-center font-semibold italic text-[24px] leading-[32px] tracking-[2%] text-[#EEEEEE]">
									A Companion
								</h1>
								<p className="font-satoshi font-light text-base leading-[28px] tracking-[1%] text-center text-[#DDDDDD]">
									Who makes learning online Faster, Organized and Interactive.
								</p>
							</div>

							<div className="flex flex-row items-center justify-center w-full bg-gradient-to-b from-transparent to-[#36150194]">
								<img src="/landingpage/geometry.svg" alt="Geometry pattern" />
							</div>
						</div>
					</div>

				</div>

				<ContentPage></ContentPage>
				<GridPage></GridPage>
				<GridPage2></GridPage2>
				<GridPage3></GridPage3>
				<GridPage4></GridPage4>
				<Vision></Vision>
				<Footer></Footer>
			</div>


		</>
	);


};

export default Page;
